import { useState, type FormEvent } from 'react';
import { formatTime } from '@brimo/shared';
import { Banner, Button, Dialog, Skeleton, TextField, TextLink, useToast } from '../../../../ds/components';
import { ApiError, errorMessage } from '../../../../lib/api';
import { useWorkspace } from '../../../../lib/session';
import { useSavedPill } from '../../SettingsFrame';
import { OrganizationPage, QueryError } from '../users/OrganizationPage';
import {
  useAddChild,
  useOrganizationTree,
  usePkvSettings,
  useRemoveChild,
  useSavePkvSettings,
  useSetBillingPayer,
  type BillingPayer,
  type TreeChild,
} from './api';
import { PkvDefaultsForm } from './PkvDefaultsForm';
import { OrganizationTreeView, PkvModuleOffBanner, TREE_TEXTS, type TreeState } from './OrganizationTreeView';
import styles from './OrganizationTreePage.module.css';

const NOT_FOUND = 'Die Organisation wurde nicht gefunden.';

/**
 * `/settings/organization/tree` (PKV M0): wires the tree view to `GET/POST/DELETE/PUT /settings/organization/tree…`
 * and the PKV defaults to `GET/PATCH /settings/organization/pkv`. `organization.updated` refreshes both through the
 * `['organization']` root.
 */
export function OrganizationTreePage() {
  const { toast } = useToast();
  const { markSaved } = useSavedPill();
  const workspace = useWorkspace();
  const pkvActive = workspace.modules.includes('pkv');
  const tree = useOrganizationTree();
  const pkvSettings = usePkvSettings(pkvActive);
  const savePkv = useSavePkvSettings();
  const setPayer = useSetBillingPayer();
  const removeChild = useRemoveChild();
  const [addOpen, setAddOpen] = useState(false);
  const [removing, setRemoving] = useState<TreeChild | null>(null);
  const [pendingChildId, setPendingChildId] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  const state: TreeState = tree.isError
    ? { status: 'error', error: tree.error }
    : tree.data
      ? { status: 'ready', tree: tree.data }
      : { status: 'pending' };

  function changePayer(child: TreeChild, billingPayer: BillingPayer) {
    setBanner(null);
    setPendingChildId(child.id);
    setPayer.mutate(
      { id: child.id, billingPayer },
      {
        onSuccess: () => {
          markSaved();
          toast({ message: `Gespeichert · ${formatTime(new Date())}` });
        },
        onError: (error) => setBanner(errorMessage(error)),
        onSettled: () => setPendingChildId(null),
      },
    );
  }

  let pkv;
  if (!pkvActive) {
    pkv = (
      <PkvModuleOffBanner
        action={
          <TextLink to="/settings/organization/modules" size="small">
            Zu den Modulen
          </TextLink>
        }
      />
    );
  } else if (pkvSettings.isError) {
    pkv = <QueryError error={pkvSettings.error} />;
  } else if (!pkvSettings.data) {
    pkv = (
      <div className={styles.pkvPanel} aria-busy="true">
        <Skeleton width="50%" />
      </div>
    );
  } else {
    pkv = (
      <div className={styles.pkvPanel}>
        <PkvDefaultsForm settings={pkvSettings.data} save={savePkv.mutateAsync} onSaved={markSaved} />
      </div>
    );
  }

  return (
    <OrganizationPage title={TREE_TEXTS.title} description={TREE_TEXTS.description}>
      {banner ? <Banner tone="danger">{banner}</Banner> : null}
      <OrganizationTreeView
        state={state}
        pendingChildId={pendingChildId}
        onAddChild={() => setAddOpen(true)}
        onRemoveChild={(child) => {
          setBanner(null);
          setRemoving(child);
        }}
        onBillingPayerChange={changePayer}
        pkv={pkv}
      />
      <AddChildDialog open={addOpen} onClose={() => setAddOpen(false)} />
      <RemoveChildDialog
        child={removing}
        pending={removeChild.isPending}
        onClose={() => {
          removeChild.reset();
          setRemoving(null);
        }}
        error={removeChild.error ? errorMessage(removeChild.error) : null}
        onConfirm={(child) =>
          removeChild.mutate(child.id, {
            onSuccess: () => {
              markSaved();
              toast({ message: `${child.name} ist nicht mehr im Baum.` });
              setRemoving(null);
            },
          })
        }
      />
    </OrganizationPage>
  );
}

/** „Unterorganisation hinzufügen": the slug of the other organization → `POST …/tree/children`. */
function AddChildDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { toast } = useToast();
  const { markSaved } = useSavedPill();
  const add = useAddChild();
  const [slug, setSlug] = useState('');
  const [error, setError] = useState<string | null>(null);

  function close() {
    setSlug('');
    setError(null);
    add.reset();
    onClose();
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    const value = slug.trim().toLowerCase();
    if (value === '') {
      setError('Bitte tragen Sie den Slug der Organisation ein.');
      return;
    }
    setError(null);
    try {
      const tree = await add.mutateAsync(value);
      const child = tree.children.find((entry) => entry.slug === value);
      markSaved();
      toast({ message: `${child?.name ?? value} ist jetzt Unterorganisation.` });
      close();
    } catch (failure) {
      if (failure instanceof ApiError && failure.status === 404) setError(NOT_FOUND);
      else if (failure instanceof ApiError && failure.fields['organizationSlug'])
        setError(failure.fields['organizationSlug']);
      else setError(errorMessage(failure));
    }
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      eyebrow={TREE_TEXTS.title}
      title={TREE_TEXTS.addChild}
      width={560}
      footer={
        <>
          <Button variant="secondary" onClick={close}>
            Abbrechen
          </Button>
          <Button type="submit" form="tree-add-child" loading={add.isPending}>
            Hinzufügen
          </Button>
        </>
      }
    >
      <form
        id="tree-add-child"
        className={styles.dialogForm}
        onSubmit={(event) => void submit(event)}
        noValidate
      >
        <TextField
          label="Slug der Organisation"
          value={slug}
          autoComplete="off"
          spellCheck={false}
          hint="Den Slug finden Sie in den Einstellungen der anderen Organisation unter Domain."
          error={error ?? undefined}
          onChange={(event) => setSlug(event.target.value)}
        />
      </form>
    </Dialog>
  );
}

interface RemoveChildDialogProps {
  child: TreeChild | null;
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: (child: TreeChild) => void;
}

/** Confirmation of „Entfernen": the servicing memberships of the own users there end with it. */
function RemoveChildDialog({ child, pending, error, onClose, onConfirm }: RemoveChildDialogProps) {
  if (child === null) return null;
  return (
    <Dialog
      open
      onClose={onClose}
      eyebrow={TREE_TEXTS.title}
      title={`${child.name} aus dem Baum entfernen?`}
      width={560}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button variant="destructiveFilled" loading={pending} onClick={() => onConfirm(child)}>
            Entfernen
          </Button>
        </>
      }
    >
      <div className={styles.dialogForm}>
        <p>Die Servicing-Zugänge Ihrer Nutzer dort werden deaktiviert.</p>
        {error ? <Banner tone="danger">{error}</Banner> : null}
      </div>
    </Dialog>
  );
}
